import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Kunden from './pages/Kunden';
import KundeDetail from './pages/KundeDetail';
import Waren from './pages/Waren';
import Bauteile from './pages/Bauteile';
import ElektrolisteForm from './pages/ElektrolisteForm';
import PoolzeichnungForm from './pages/PoolzeichnungForm';
import Berichte from './pages/Berichte';
import BerichtForm from './pages/BerichtForm';
import Inbetriebnahmeprotokolle from './pages/Inbetriebnahmeprotokolle';
import InbetriebnahmeForm from './pages/InbetriebnahmeForm';
import ProtokolKonfiguration from './pages/ProtokolKonfiguration';
import Karte from './pages/Karte';
import ChangePassword from './pages/ChangePassword';
import Aufgaben from './pages/Aufgaben';
import Potenziale from './pages/Potenziale';
import Beratungsanfrage from './pages/Beratungsanfrage';
import Beratungstermine from './pages/Beratungstermine';
import Stempeln from './pages/Stempeln';
import Zeiterfassung from './pages/Zeiterfassung';
import Arbeitszeiten from './pages/Arbeitszeiten';
import Urlaub from './pages/Urlaub';
import Personalkonfiguration from './pages/Personalkonfiguration';
import MenueKonfiguration from './pages/MenueKonfiguration';
import NotFound from './pages/NotFound';
import './App.css';

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <Toaster position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Öffentliches Event-Formular (iPad-Kiosk) – ohne Login, ohne Menü */}
            <Route path="/event" element={<Beratungsanfrage />} />
            {/* Stempeln per QR-Deeplink mit geheimem Token (Login nötig, ohne Menü) */}
            <Route
              path="/stempeln/:token"
              element={
                <ProtectedRoute>
                  <Stempeln />
                </ProtectedRoute>
              }
            />
            <Route
              path="/arbeitszeiten"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Arbeitszeiten />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/urlaub"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Urlaub />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal-konfiguration"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Personalkonfiguration />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/menue-konfiguration"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <MenueKonfiguration />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/kunden"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Kunden />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/kunden/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <KundeDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/waren"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Waren />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/bauteile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Bauteile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/elektrolisten/neu"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ElektrolisteForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/elektrolisten/:id/bearbeiten"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ElektrolisteForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/poolzeichnungen/neu"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PoolzeichnungForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/poolzeichnungen/:id/bearbeiten"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PoolzeichnungForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/berichte"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Berichte />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/berichte/neu"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BerichtForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/berichte/:id/bearbeiten"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BerichtForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/protokolle"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Inbetriebnahmeprotokolle />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/protokolle/neu"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InbetriebnahmeForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/protokolle/:id/bearbeiten"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InbetriebnahmeForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/protokoll-konfiguration"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <ProtokolKonfiguration />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/zeiterfassung"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Zeiterfassung />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/karte"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Karte />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChangePassword />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/aufgaben"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Aufgaben />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/potenziale"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Potenziale />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/beratungstermine"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Beratungstermine />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
